import { ref, computed } from 'vue'
// CATALOG_REGISTRY, CACHEKEY_TO_SLUG y getCatalogEntry llegan por auto-import
// de Nuxt (carpeta constants/).

// Estado y helpers para invalidar la cache de los catalogos "planos"
// (ver constants/catalogs.ts y composables/ui/useCatalogoPlano.ts).
//
// Al invalidar un catalogo:
//   1. se borran sus claves de localStorage (canonica + alias historicos)
//   2. se incrementa un contador reactivo por slug -> los useCatalogoPlano
//      montados y suscritos a ese slug recargan desde el servidor
//   3. se deja una marca en localStorage -> el plugin catalog-cache.client.ts
//      propaga la invalidacion a las demas pestañas del mismo navegador
//
// El contador vive en un ref a nivel de modulo (singleton en cliente) y NO en
// useState: invalidateCatalog() se llama despues de awaits (fetch + sweetalert)
// donde el contexto de Nuxt ya no esta disponible, y useState fallaria ahi.

/** Clave de localStorage usada solo como "ping" cross-tab (evento storage). */
export const CATALOG_INVALIDATE_PING_KEY = 'catalog-cache-invalidate'

const versions = ref<Record<string, number>>({})

/** Contador reactivo de invalidaciones para un slug (0 si nunca se invalido). */
export function catalogVersion(slug: string) {
  return computed(() => versions.value[slug] ?? 0)
}

function bumpVersion(slug: string) {
  versions.value = { ...versions.value, [slug]: (versions.value[slug] ?? 0) + 1 }
}

/** Nombre con el que useCatalogs().getCatalog() podria haber cacheado este
 *  catalogo (usa `/${name}` como URL). Best-effort: el ultimo segmento del
 *  endpoint sin query string. */
function catalogsStateName(endpoint: string): string {
  return endpoint.replace(/^\//, '').split('?')[0]
}

/**
 * Invalida la cache de uno o varios catalogos.
 *
 * `slug` es el mismo string que el `type` de <DialogForm> / el `entityKey` de
 * useCrudDelete (p. ej. 'measurements', 'machines', 'processes'). Si el slug no
 * esta en el registro, es un no-op seguro.
 */
export function invalidateCatalog(slug: string | string[]) {
  // useCrudDelete recibe la clave de i18n (p.ej. 'machines_types'); el registro usa guiones.
  const slugs = (Array.isArray(slug) ? slug : [slug])
    .map((s) => getCatalogEntry(s) ? s : s.replace(/_/g, '-'))
    .filter((s) => !!getCatalogEntry(s))

  if (!slugs.length) return

  if (import.meta.client) {
    for (const s of slugs) {
      const entry = getCatalogEntry(s)!
      for (const key of entry.cacheKeys) localStorage.removeItem(key)
    }

    // El cache en memoria de useCatalogs() (useState) se limpia si hay contexto
    // de Nuxt disponible; si no, se ignora (ese cache es secundario).
    try {
      const catalogs = useCatalogs()
      for (const s of slugs) {
        catalogs.clearByName?.(catalogsStateName(getCatalogEntry(s)!.endpoint))
      }
    } catch { /* sin contexto de Nuxt: ignorar */ }
  }

  for (const s of slugs) bumpVersion(s)

  if (import.meta.client) {
    // El timestamp garantiza que newValue cambie siempre y dispare 'storage'.
    try {
      localStorage.setItem(
        CATALOG_INVALIDATE_PING_KEY,
        JSON.stringify({ slugs, t: Date.now() })
      )
    } catch { /* localStorage lleno / bloqueado: ignorar */ }
  }
}

/**
 * Aplica una invalidacion originada en OTRA pestaña. Esa pestaña ya borro las
 * claves de localStorage; aca solo hay que refrescar el contador reactivo local.
 * Lo usa el plugin catalog-cache.client.ts.
 */
export function applyRemoteCatalogInvalidation(slugs: string[]) {
  for (const s of slugs) {
    if (getCatalogEntry(s)) bumpVersion(s)
  }
}

/** slug logico a partir de una cacheKey (canonica o alias), o undefined. */
export function slugFromCacheKey(cacheKey: string): string | undefined {
  return CACHEKEY_TO_SLUG[cacheKey]
}
